import { longStaleTime } from 'BrokerTsLib/src/hooks'
import apiClient from 'BrokerTsLib/src/Utils/apiClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useUrl } from '.'
import { Apis } from '../appSettings/AppSettings'

export interface PublicEmailLoginInfo {
  idEmailMessage: number
  paramsId: number
  isRcLoginType: boolean
  isForeigner: boolean
  emailTypeStr: string
  salutation: string
  validTo: string
  contractNumber?: string
  privacyNoticeDocumentUrl?: string
}

export interface PublicEmailLoginRequest {
  idEmailMessage: number
  paramsId: number
  password: string
}

export interface PublicEmailContent {
  idEmailMessage: number
  body: string
  subject: string
  validTo: string
  attachments: Array<{
    id: number
    name: string
    contentType: string
  }>
}

export function getLoginTypeDescription(isRcLoginType: boolean, isForeigner: boolean): string {
  if (isRcLoginType && isForeigner) return 'data narození (rok, měsíc, den)'
  if (isRcLoginType) return 'RČ'
  return 'IČO'
}

export const useGetPublicEmailLoginInfo = (token?: string) => {
  const url = useUrl(Apis.publicEmail)

  return useQuery({
    queryKey: ['useGetPublicEmailLoginInfo', token],
    queryFn: async () => {
      return await apiClient.get<PublicEmailLoginInfo>(`${url}/EmailLogIn`, {
        params: { q: token }
      }).then(res => res.data)
    },
    staleTime: longStaleTime,
    enabled: !!token
  })
}

export const usePublicEmailSignIn = () => {
  const url = useUrl(Apis.publicEmail)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: PublicEmailLoginRequest) => apiClient.post(`${url}/EmailLogIn`, data),
    onSuccess: () => {
      queryClient.setQueriesData({ queryKey: ['isPublicEmailLogged'] }, { logged: true })
    }
  })
}

export const usePublicEmailSignOut = () => {
  const url = useUrl(Apis.publicEmail)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post(`${url}/SignOut`).then(res => res.data),
    onSuccess: () => {
      queryClient.setQueriesData({ queryKey: ['isPublicEmailLogged'] }, { logged: false })
      queryClient.invalidateQueries({ queryKey: ['useGetPublicEmailContent'] })
    }
  })
}

export const useGetPublicEmailContent = () => {
  const url = useUrl(Apis.publicEmail)

  return useQuery({
    queryKey: ['useGetPublicEmailContent'],
    queryFn: async () => {
      return await apiClient.get<PublicEmailContent>(`${url}/Email`).then(res => res.data)
    },
    staleTime: longStaleTime,
    retry: false
  })
}

export const getPublicEmailAttachmentUrl = (idEmailAttachement: number, baseUrl: string) => {
  return `${baseUrl}/EmailAttachement?idEmailAttachement=${idEmailAttachement}`
}

export const getPublicEmailAttachmentZipUrl = (baseUrl: string) => {
  return `${baseUrl}/EmailAttachementZip`
}
